import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { concat, forkJoin, merge, Observable, of, Subject, switchMap, tap, zip } from 'rxjs';
import { ConfigService } from '../config.service';
import { TripType } from '../ui/dropdowns/dropdown-option.service';
import { BackgroundCheckTypes, GlInfo, MinistryHoursRow, PotentialGl, Trip, TripDetail } from './commonInterfaces';
import { DataTransform, DateField } from './dataTransformFunctions';
import { McDataMapService } from './mc-data-map.service';
import { TripDetailService } from './trip-detail.service';

export const CONST_tripUpdated = new Subject<TripUpdatedArgs>();

export const tripDateFields: DateField<Partial<Trip>>[] = [
  { fieldName: "tripDate", dateType: "local" },
  { fieldName: "dateReserved", dateType: "central" },
  { fieldName: "tripEndDate", dateType: "local"}
]

export const  tripDetailDateFields: DateField<TripDetail>[] = [
  { fieldName: "tripDate", dateType: "local" },
  { fieldName: "dateReserved", dateType: "central" },
  { fieldName: "tripEndDate", dateType: "local"}
]


interface TripAssignedArgs {
  customerId: number
}

export interface TripUpdatedArgs {
  trip: Trip,
  tripDetail: TripDetail,
  updateValues?: Partial<Trip>
}

export interface RosterDetails {
  rosterSize: number,
  attendeeCount: number
}


export interface AssignTripGlInfo {
  type: "existing" | "from-contact" | "none",
  newuserId?: number,
  contactId?: number
}
export interface AssignTripParams {
  custId: number,
  salesId: number,
  assistId: number,
  locId: string,
  airport: string,
  accommodations?: string,
  duration: number,
  cost: number,
  individual: number,
  showTj: number,
  leaderTrip: number,
  leaderMission: number,
  leaderSponsor: number,
  fmsc: number,
  youthCartel: number,
  attendees: number
  glInfo: AssignTripGlInfo,
  tripDate: Date,
  pTripId: number,
  officeId: number,
  transferredTripId: number,
  designation: number,
  automatedConsultingEmails: number,
  disaster: number
}

export interface ParticipantsByDateResponse {
  date: string;
  attendees: number;
}

export interface TripParticipantSummary {
  tripId: number;
  custId: number;
  groupName: string;
  tripDate: Date;
  foundAttendees: number;
  expectedAttendees: number;
  forms: boolean;
  legals: boolean;
  shirts: boolean;
  backgroundCheck: boolean;
  destination: string;
}

@Injectable({
  providedIn: 'root'
})
export class TripService {

  tripUpdated = CONST_tripUpdated

  private tripAssigned = new Subject<TripAssignedArgs>()
  tripAssigned$ = this.tripAssigned.asObservable()

  tripDateFields = tripDateFields
  tripDetailDateFields = tripDetailDateFields


  constructor(
    private http: HttpClient,
    private config: ConfigService,
    private mcDataMapService: McDataMapService,
    private tripDetailService: TripDetailService
  ) { }

  readonly checkInStart: Date = new Date(2022, 0, 1);

  getTrip(tripId: number) {
    return this.http.get<Trip>(`${this.config.dataApiUrl}/trips/${tripId.toString()}/trip`).pipe(
      switchMap(trip => {
        return DataTransform.replaceDates<Trip>(trip, this.tripDateFields)
      }),
      switchMap(t => {
        return this.addTripType(t)
      }))

  }


  updateTrip(tripId: number, tripData: Partial<Trip>): Observable<TripUpdatedArgs> {
    return this.http.patch<TripUpdatedArgs>(`${this.config.dataApiUrl}/trips/${tripId.toString()}`, DataTransform.replaceDatesForUpdate<Partial<Trip>>(tripData, this.tripDateFields)).pipe(
      switchMap(args => {
       return forkJoin([
          DataTransform.replaceDates<Trip>(args.trip, this.tripDateFields),
          DataTransform.replaceDates<TripDetail>(args.tripDetail, this.tripDetailDateFields)
        ]
        ).pipe(
          switchMap(modifiedResults => {
            return of({
              trip: modifiedResults[0],
              tripDetail: modifiedResults[1]
            })
          })
        )

      }),
      tap((updatedTrip) => {
        this.tripUpdated.next({...updatedTrip, updateValues: tripData})
      })
    )
  }



  updateTripResult(tripId: number) {
    return forkJoin([
      this.getTrip(tripId).pipe(
        switchMap(t => DataTransform.replaceDates<Trip>(t, this.tripDateFields)),
        switchMap(t => this.addTripType(t))),
      this.tripDetailService.getTripById(tripId).pipe(switchMap(td => DataTransform.replaceDates(td, this.tripDetailDateFields)))
    ]).pipe(
      switchMap(results => {
        return of({
          trip: results[0],
          tripDetail: results[1]
        })
      }),
      tap((updatedTrip => this.tripUpdated.next(updatedTrip)))
    )
  }

  getRosterDetails(tripId: number) {
    return this.http.get<RosterDetails>(`${this.config.dataApiUrl}/trips/${tripId.toString()}/rosterDetails`)
  }

  assignTrip(tripData: AssignTripParams) {
 
    const postData = {
      ...tripData,
      tripDate: DateTime.fromJSDate(tripData.tripDate).toFormat("yyyy-MM-dd")

    }

    return this.http.post(`${this.config.dataApiUrl}/trips/trip`, postData).pipe(
      tap(result => {
        this.tripAssigned.next({ customerId: tripData.custId })
      })
    )
  }

  createIndividualReadinessTrip(data: {newuserId: number, readinessTripId: number, dates: DateTime[]}) {

    const postData = {
      ...data,
      dates: data.dates.map(d => d.toFormat("yyyy-MM-dd"))
    }

    return this.http.post<Trip>(`${this.config.dataApiUrl}/readiness-trips/individual-trip`, postData).pipe(
      switchMap(trip => {
        return DataTransform.replaceDates(trip, this.tripDateFields )
      })
    )
  }

  //NOTE: This function looks weird b/c it used to communicate with the Coldfusion API.  We could probably optimize it to a single call at some point,
  //by moving the logic that keeps only a single type selected to the backend, but it's not worth it for now.
  updateTripType(tripId: number, custId: number, newType: TripType) {
    let observables: Observable<any>[] = []

    return this.getTrip(tripId).pipe(
      switchMap(
        trip => {
         
          let updateObj: Partial<Trip> = {fmsc: 0}
          if (trip.fmsc === 1) {
            observables.push(this.http.patch<TripUpdatedArgs>(`${this.config.dataApiUrl}/trips/${tripId}`, updateObj))
          }

          if (trip.special === 1) {
            updateObj = { special: 0 }
            observables.push(this.http.patch<TripUpdatedArgs>(`${this.config.dataApiUrl}/trips/${tripId}`, updateObj))
          }

          if (trip.leaderTrip === 1 && (newType !== 'leaderMission' && newType !== 'leaderSponsor')) {
            updateObj = { leaderTrip: 0 }
            observables.push(this.http.patch<TripUpdatedArgs>(`${this.config.dataApiUrl}/trips/${tripId}`, updateObj))
          }

          if (trip.leaderMission === 1) {
            updateObj = { leaderMission: 0 }
            observables.push(this.http.patch<TripUpdatedArgs>(`${this.config.dataApiUrl}/trips/${tripId}`, updateObj))
          }

          if (trip.leaderSponsor === 1) {
            updateObj = { leaderSponsor: 0 }
            observables.push(this.http.patch<TripUpdatedArgs>(`${this.config.dataApiUrl}/trips/${tripId}`, updateObj))
          }

          if (trip.disaster === 1) {
            updateObj = { disaster: 0 }
            observables.push(this.http.patch<TripUpdatedArgs>(`${this.config.dataApiUrl}/trips/${tripId}`, updateObj))
          }

          if (trip.yc === 1) {
            updateObj = { yc: 0 }
            observables.push(this.http.patch<TripUpdatedArgs>(`${this.config.dataApiUrl}/trips/${tripId}`, updateObj))
          }

          if (newType === 'leaderMission' || newType === 'leaderSponsor') {
            updateObj = { leaderTrip: 1 }
            observables.push(this.http.patch<TripUpdatedArgs>(`${this.config.dataApiUrl}/trips/${tripId}`, updateObj))
          }

          if (newType !== "ppm") {
            updateObj = { [newType]: 1 }
            observables.push(this.http.patch<TripUpdatedArgs>(`${this.config.dataApiUrl}/trips/${tripId}`, updateObj))

          }

          return forkJoin(observables).pipe(
            switchMap(result => {
              return this.updateTripResult(tripId)
            })
          )

        })
    )


  }

  addTripType(trip: Trip): Observable<Trip> {

    const returnTrip = { ...trip }

    if (returnTrip.fmsc === 1) {
      returnTrip.tripType = "fmsc"
    }
    else if (returnTrip.special === 1) {
      returnTrip.tripType = "special"
    }

    else if (returnTrip.leaderMission === 1) {
      returnTrip.tripType = "leaderMission"
    }

    else if (returnTrip.leaderSponsor === 1) {
      returnTrip.tripType = "leaderSponsor"
    }

    else if (returnTrip.leaderTrip === 1) {
      returnTrip.tripType = "leaderTrip"
    }

    else if (returnTrip.disaster === 1) {
      returnTrip.tripType = "disaster"
    }
    else if (returnTrip.yc === 1) {
      returnTrip.tripType = "yc"
    }
    else {
      returnTrip.tripType = "ppm"
    }

    return of(returnTrip)
  }

  addTripTypeArray(arr: Trip[]) {
    let observables: Observable<Trip>[] = []
    for (const item of arr) {
      observables = [...observables, this.addTripType(item)]
    }

    if (observables.length === 0) {
      return of([])
    }
    else {
      return zip(observables)
    }

  }

  tripHasEnded(tripEndParams: Pick<Trip, "tripDate" | "duration">) {
    return DateTime.fromJSDate(tripEndParams.tripDate).plus({ days: tripEndParams.duration - 1 }) < DateTime.now().startOf("day")
  }

  emptyTrip(): Trip {
    return {
      id: 0,
      officeId: 0,
      tripDate: new Date(1000, 0, 1),
      tripEndDate: new Date(1000, 0, 1),
      destination: "string",
      duration: 0,
      accommodations: "",
      cost: 0,
      consult: "",
      custId: 0,
      dateReserved: new Date(1000, 0, 1),
      staffId: 0,
      siteLeaderId: 0,
      salesId: 0,
      attendees: 0,
      attendeesOriginal: 0,
      guestUser: "",
      grouping: "",
      lodging: "",
      anchorage: "",
      caye: "",
      bus: "",
      newReg: 0,
      cbWelcomeCall: 0,
      cbPromo: 0,
      cbTshirts: 0,
      cbCustForms: 0,
      cbFlightInfo: 0,
      cbGoodToGo: 0,
      cbLiabilityForm: 0,
      individual: 0,
      showTj: 0,
      airport: "",
      minMoneyEst: 0,
      ministryMoney: 0,
      mmLock: 0,
      quickNote: "",
      vehicle: "",
      meals: "",
      expenses: "",
      itinerary: "",
      itineraryPub: 0,
      special: 0,
      cbPack: 0,
      translator: "",
      terms: 0,
      fmsc: 0,
      ppm: 0,
      consultPub: 0,
      tlPub: 0,
      salesAssist: 0,
      promo: "",
      consultId: 0,
      active: 0,
      cutoff: 0,
      dropped: 0,
      dropReason: "",
      noInvoice: 0,
      yc: 0,
      driving: 0,
      translatorConfirmed: 0,
      lodgingConfirmed: 0,
      cayeConfirmed: 0,
      anchorageConfirmed: 0,
      busConfirmed: 0,
      vehicleConformed: 0,
      mealsConfirmed: 0,
      groupingConfirmed: 0,
      airportConfirmed: 0,
      partPay: 0,
      tripDonate: 0,
      travelPay: 0,
      partDonate: 0,
      partDonatePublic: 0,
      adminConsult: 0,
      locId: "",
      subtrips: 0,
      recommendedJournal: 0,
      displayAfterCutoff: 0,
      consultantNote: "",
      timestamp: new Date(1000, 0, 1),
      lastInvoiced: new Date(1000, 0, 1),
      lastAttendeeChange: new Date(1000, 0, 1),
      extendDonation: 0,
      renewal: 0,
      glJournals: 0,
      consultingEmails: 0,
      leaderTrip: 0,
      leaderMission: 0,
      leaderSponsor: 0,
      termsConditionsUrl: "",
      termsConditionsTimestamp: new Date(1000, 0, 1),
      termsConditionsUserId: 0,
      covidPaymentAgreementUrl: "",
      covidPaymentAgreementTimestamp: "",
      covidPaymentAgreementUserId: 0,
      fundraisingAgreementUrl: "",
      fundraisingAgreementTimestamp: new Date(1000, 0, 1),
      shirtsNote: "",
      shirtOrderNum: "",
      guideOrderNum: "",
      disaster: 0,
      transferredTripId: 0,
      inAbacus: 0,
      hasGroupPhoto: 0,
      hasGroupThumbnail: 0,
      tripType: "ppm",
      designation: 0,
      backgroundCheckType: BackgroundCheckTypes.SelfManaged,
      waiveRegiFee: 0
    }

  }

  getParticipantsByDate(startDate: Date, endDate: Date, locationIds: string[]) {
    
    
    return this.http.get<ParticipantsByDateResponse[]>(`${this.config.dataApiUrl}/trips/participants/by-date`, 
      { params: { startDate: this.formatDate(startDate), endDate: this.formatDate(endDate, true), locationIds: locationIds } })
  }

  getParticipantReportData(startDate: Date, endDate: Date) {
    return this.http.get<TripParticipantSummary[]>(`${this.config.dataApiUrl}/trips/participants/report`,
      { params: { startDate: this.formatDate(startDate), endDate: this.formatDate(endDate, true) } }
    )
  }

  formatDate(date: Date, isEndDate: boolean = false) {
    const dt = DateTime.fromJSDate(date);
    const adjustedDt = isEndDate ? dt.endOf('day') : dt.startOf('day');
    return adjustedDt.toFormat('yyyy-MM-dd\'T\'HH:mm:ss');
  };

  getMinistryHours(startDate: Date, endDate: Date) {
    return this.http.get<MinistryHoursRow[]>(`${this.config.dataApiUrl}/reports/ministry-hours`,
      { params: { startDate: DateTime.fromJSDate(startDate).toFormat('yyyy-MM-dd'), endDate: DateTime.fromJSDate(endDate).toFormat('yyyy-MM-dd') } }
    )
  }

}
